import { FulfillmentMeta } from 'src/app/assessment/model/assessment';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AssessmentFulfillmentListService } from 'src/app/core/service/grid-data/grid-data-assessment-fulfillment.service';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { State, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { DocumentService } from 'src/app/core/service/document.service';
import { saveAs } from '@progress/kendo-file-saver';
import { UntypedFormGroup } from '@angular/forms';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { SettingService } from 'src/app/core/service/setting.service';
import { AssessmentFulfillmentData } from 'src/app/member/model/assessment-fulfillment';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { ReferralComponent, ReferralEvent } from 'src/app/referral/referral.component';
import { PDFViewerTool } from '@progress/kendo-angular-pdfviewer';
import { MemberService } from 'src/app/core/service/member.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { UserService } from 'src/app/core/service/user.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ResizedEvent } from '../../resize-event/resized-event';
import { Base64Util } from 'src/app/core/utility/base64-util';
import { Base64 } from 'js-base64';

@Component({
  standalone: false,
  selector: 'app-member-assessment',
  templateUrl: './member-assessment.component.html',
  styleUrls: ['./member-assessment.component.scss']
})
export class MemberAssessmentComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('survey') public survey: ElementRef;
  @ViewChild('referral') public referral: ReferralComponent;
  @ViewChild('formlyDiv') public formlyDiv: ElementRef;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  @Input() public memberId: number;
  @Input() public taskId: number = null;
  @Input() public assessmentId: number = null;

  @Output() public refresh: EventEmitter<any> = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public submitting = false;
  public patViewExtensions: GridViewManager;
  public isShowMemberExtension = false;
  public defaultMaxExtensionHeight = 600;
  public currentMaxExtensionHeight = this.defaultMaxExtensionHeight;
  public maxWindowHeight = undefined;

  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public currentCaseId = null;
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public isNewAssessment = false;
  public loadingDocument: boolean;
  public isSupportedDocumentType: boolean;
  public documentImage: any;
  public documentContent: string;
  public documentDialogOpened: boolean;
  public documentDescription: string;
  public documentMediaType: string;
  public enableAdvanced = false;
  public taskDialogOpened = false;
  public selectedAssessmentFulfillment: any;
  public deleteDialogOpened = false;
  public memberAssessmentCollapsed = false;
  public serviceCategories = [];
  public communityEnabled = false;
  public windowState = 'default';
  public showMemberExtension = false;
  public deleteAssessmentsEnabled = false;
  public viewExtensionsShowDisplayName = false;
  public viewExtensionsShowAlternateValue = false;
  public addCaseAssessmentsEnabled = false;
  public addClientAssessmentsEnabled = false;
  public assessmentCommentsLoaded = false;

  // Formly model and fields
  public model = {};
  public fields: FormlyFieldConfig[] = [];
  public fieldsMeta: FormlyFieldConfig[] = [];
  public form = new UntypedFormGroup({});
  public pdfViewerTools: PDFViewerTool[] = [
    /*Disable search for because it a bit slow with pdf and multiple pages
    'search',*/
    'selection',
    'spacer',
    'zoom',
    'zoomInOut',
    'spacer',
    'download',
    'print',
    'pager',
  ];
  public dialogVisible: boolean = false;

  private listSubscription: Subscription;
  private memberAssessmentId: number;
  private messagingSubscription: Subscription;

  constructor(
    private assessmentFulfillmentListService: AssessmentFulfillmentListService,
    private memberService: MemberService,
    private extensionService: ExtensionService,
    private documentService: DocumentService,
    private messengerService: MessengerService,
    private assessmentService: AssessmentService,
    private messageHandlerService: MessageHandlerService,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private settingService: SettingService) { }

  public get loading(): boolean
  {
    return this.assessmentFulfillmentListService.loading;
  }

  @Input() public set caseId(value: any)
  {
    const previousCaseId = this.currentCaseId;
    this.currentCaseId = value;
    // Must reload assessment when swiching from on case to another
    if (previousCaseId !== null && previousCaseId !== this.currentCaseId)
    {
      this.getAssessmentFulfillments(this.memberId);
    }
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.deleteAssessmentsEnabled = (sessionSetting.deleteAssessmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.deleteAssessmentsRoles) : false;
        this.addClientAssessmentsEnabled = (sessionSetting.addClientAssessmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addClientAssessmentsRoles) : false;
        this.addCaseAssessmentsEnabled = (sessionSetting.addCaseAssessmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addCaseAssessmentsRoles) : false;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'openAddAssessmentDialog')
        {
          this.addHandler();
        }
        else if (message.type === 'reloadMemberAssessments')
        {
          this.getAssessmentFulfillments(this.memberId);
        }
      }
    });

    this.listSubscription = this.assessmentFulfillmentListService.subscribe({
      next: (value: AssessmentFulfillmentData) =>
      {
        this.assessmentCommentsLoaded = false;
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            d.assessmentDate = new Date(d.assessmentDate);
            d.lastModifiedOn = new Date(d.lastModifiedOn);
            d.createdOn = new Date(d.createdOn);
            if (d.assessmentComments)
            {
              this.assessmentCommentsLoaded = true;
            }
          }
          );
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
      }
    });
    this.getAssessmentFulfillments(this.memberId);
  }

  public ngAfterViewChecked(): void
  {
    this.updateSplitterBar();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Output Event handler to set the loaded number of assessments in the header.
   *
   * @param count Number of assessment loaded.
   */
  public setTotal(count: number): void
  {
    this.total = count !== undefined ? count : 0;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.assessmentFulfillmentListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(currentFilter: CompositeFilterDescriptor): void
  {
    this.state.filter = currentFilter;
    this.assessmentFulfillmentListService.queryByState(this.state);
  }

  public addHandler(): void
  {
    this.isNewAssessment = true;
  }

  /**
   * View document handler
   *
   * @param dataItem: Document to view
   */
  public viewHandler(dataItem: any): void
  {
    // string, description: string, mediaType: string, createdOn: string
    document.body.style.overflow = 'hidden';

    // Reset document content variables
    this.memberAssessmentId = dataItem.id;
    this.selectedAssessmentFulfillment = dataItem.assessmentFulfillment;
    this.documentMediaType = dataItem.assessmentFulfillment.mimeType;
    this.documentDescription = dataItem.assessmentFulfillment.mimeType === 'application/json' ?
      dataItem.name : dataItem.assessmentFulfillment.name;
    this.documentContent = '';
    this.documentImage = '';

    this.isShowMemberExtension = false;
    this.loadingDocument = true;
    this.documentDialogOpened = true;

    this.openDocument(dataItem.assessmentFulfillment.url, dataItem.assessmentFulfillment.mimeType, dataItem?.showMemberExtension || false);
  }

  /**
   * Save document to local
   *
   * @param url Document url
   * @param name Document file name
   */
  public saveFileLocal(url: string, name: string): void
  {
    this.documentService.retrieveDocument(url).subscribe({
      next: (data) =>
      {
        saveAs(`data:${data.mimeType};base64,${data.content}`, name);
        this.refresh.emit();
      }
    });
  }

  /**
   * Open the document viewer page
   *
   * @param url Document url
   * @param mediaType Document mime type
   */
  public openDocument(url: string, mediaType: string, showMemberExtension: boolean = false): void
  {
    this.documentService.retrieveDocument(url).subscribe({
      next: (data) =>
      {
        this.loadingDocument = false;
        this.isSupportedDocumentType = true;
        switch (mediaType)
        {
          case 'image/bmp':
          case 'image/png':
          case 'image/jpeg':
          case 'image/jpg':
          case 'image/jpe':
            this.documentImage = `data:${mediaType};base64,${data.content}`;
            if (mediaType.includes('image/jp'))
            {
              this.documentMediaType = 'image/jpg';
            }
            break;
          case 'application/pdf':
            this.documentContent = `data:${mediaType};base64,${data.content}`;
            break;
          case 'application/json':
            this.showMemberExtension = showMemberExtension;
            this.setFormlyFieldsAndModel(data.content);
            // Work around to set the focus on the first control to let the select to load it's value
            setTimeout(() =>
            {
              this.windowState = 'maximized';
              if (this.survey?.nativeElement &&
                this.survey?.nativeElement?.childNodes && this.survey?.nativeElement?.childNodes.length > 0 &&
                this.survey.nativeElement.childNodes[0].elements && this.survey.nativeElement.childNodes[0].elements.length > 0)
              {
                this.survey.nativeElement.childNodes[0].elements[0].focus();
              }
            }, 500);
            break;
          case 'text/plain':
            this.documentContent = Base64.decode(data.content);
            break;
          default:
            this.isSupportedDocumentType = false;
            break;
        }
      }
    });
  }

  /**
   * Close the document viewer modal
   */
  public closeDocumentViewerDialog(): void
  {
    this.documentDialogOpened = false;
    document.body.style.overflow = '';
  }

  public openDialog(): void
  {
    if (this.form.dirty)
    {
      this.dialogVisible = true;
    }
    else
    {
      this.cancelSave(true);
    }
  }

  public cancelSave(confirm: boolean): void
  {
    if (confirm)
    {
      this.closeDocumentViewerDialog();
    }
    this.dialogVisible = false;
  }

  /**
   * Method called after assessment fulfillment has been added.
   */
  public afterAssessmentFulfillmentAdded(data: any): void
  {
    this.isNewAssessment = false;
    this.getAssessmentFulfillments(this.memberId);
    if (data)
    {
      // If a patient mood was saved, then we need to reload the patient history.
      if (data.mood)
      {
        this.messengerService.sendMessage(new MessageSubject('reloadContactHistory'));
      }

      // If a flag was saved, then we need to reload the patient flag.
      if (data.flag)
      {
        this.messengerService.sendMessage(new MessageSubject('reloadMemberFlags'));
      }

      // Keep service categories for community referrals
      this.serviceCategories = data.serviceCategories ? data.serviceCategories : [];
      if (data.addFollowUpTask)
      {
        this.taskDialogOpened = true;
      }
      else
      {
        // If task followUp, the referral will be performed after the task being added or task add dialog being cancelled.
        this.processReferrals(this.serviceCategories);
      }
      this.refresh.emit();
    }
  }

  /**
   * Method called after a new task has been added.
   */
  public afterTaskAdded(): void
  {
    this.taskDialogOpened = false;
    this.messengerService.sendMessage(new MessageSubject('reloadMemberTasks'));
    this.refresh.emit();
    this.processReferrals(this.serviceCategories);
  }

  /**
   * Method called when follow up task add is cancelled.
   */
  public taskAddCancelled(): void
  {
    this.taskDialogOpened = false;
    this.processReferrals(this.serviceCategories);
  }

  /**
   * Indicates if the grid details visibility. Details will show only if the assessments has comments.
   *
   * @param dataItem Grid dataItem
   * @param index Index of the data in the grid list.
   */
  public showOnlyAssessmentComments(dataItem: any): boolean
  {
    return dataItem.assessmentComments;
  }

  /**
   * Sets the assessment fulfillment to delete, and opens the dialog.
   */
  public deleteHandler(assessmentFulfillment: any): void
  {
    this.selectedAssessmentFulfillment = assessmentFulfillment;
    this.open('deleteDialog');
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    const assessmentFulfillmentId = this.selectedAssessmentFulfillment.id;
    if (confirm)
    {
      // Proceed with the deletion
      this.assessmentService.deleteAssessmentFulfillment(assessmentFulfillmentId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('ASSESSMENT-FULFILLMENT-DELETED-SUCCESSFULLY');
          this.getAssessmentFulfillments(this.memberId);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedAssessmentFulfillment = null;
  }

  /**
   * Update an existing assessment.
   */
  public updateAssessment(): void
  {
    this.submitting = true;

    const objectToPersist: any = {};
    objectToPersist.memberId = this.memberId;

    // Send assessment fulfillment
    objectToPersist.assessmentFulfillment = {};
    objectToPersist.assessmentFulfillment.documentId = this.selectedAssessmentFulfillment.documentId;

    const meta: FulfillmentMeta = new FulfillmentMeta();
    meta.data = this.model;                     // Add data model to json object
    meta.fields = this.fieldsMeta;              // Must keep a clean version of fields definitions for updates

    objectToPersist.assessmentFulfillment.name = this.selectedAssessmentFulfillment.name;
    objectToPersist.assessmentFulfillment.mimeType = 'application/json';
    objectToPersist.assessmentFulfillment.file = Base64Util.encodeUnicode(JSON.stringify(meta));
    objectToPersist.assessmentDate = moment.utc().toDate();
    objectToPersist.name = this.documentDescription;
    objectToPersist.id = this.memberAssessmentId;

    this.assessmentService.updateAssessmentFulfillment(objectToPersist).subscribe({
      next: (updatedAssessement) =>
      {
        this.messageHandlerService.displaySuccess('ASSESSMENT-SAVED-SUCCESSFULLY');
        this.closeDocumentViewerDialog();

        // Manually find the row in the grid and update
        const index = this.gridView.data.findIndex(x => x.id === updatedAssessement.id);
        if (index >= 0)
        {
          this.gridView.data[index].lastModifiedOn = new Date(updatedAssessement.lastModifiedOn);
          this.gridView.data[index].lastModifiedBy = updatedAssessement.lastModifiedBy;
        }

        // Keep service categories for referral
        this.serviceCategories = updatedAssessement.serviceCategories ? updatedAssessement.serviceCategories : [];
        this.processReferrals(this.serviceCategories);
      },
      error: () =>
      {
        this.messageHandlerService.displayError('ASSESSMENT-SAVE-ERROR');
      },
      complete: () =>
      {
        this.submitting = false;
        this.selectedAssessmentFulfillment = null;
      }
    });
  }

  /**
   * After processing the Referral, the case will be completed if this is a referral reassignment.
   */
  public afterReferralProcessing(e: ReferralEvent): void
  {
    this.referral.completeReferralReassigment(e);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public patExtensionsDataStateChange(state: DataStateChangeEvent): void
  {
    this.patViewExtensions.setState(state);
  }

  public formatExtensionValue(dataItem: any): string
  {
    return this.extensionService.formatExtensionValue(dataItem);
  }

  /**
   * This method is to set the person extensions height
   *
   * @param e ResizedEvent
   */
  public onResized(e: ResizedEvent): void
  {
    setTimeout(() =>
    {
      if (this.formlyDiv)
      {
        const tabHeight = 0;
        this.maxWindowHeight = e.newHeight - 170 - tabHeight;
        this.currentMaxExtensionHeight = this.maxWindowHeight + 10;
      }
    }, 500);
  }

  public onStateChange(state: string): void
  {
    this.windowState = state;
  }

  public updateSplitterBar()
  {
    // do not run if no splitter is loaded
    if (!this.splitter) { return; }

    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetWidth : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  /**
   * Proceed with Community referral for the categories returned from the new/updated assessments.
   *
   * @param serviceCategories Array of service categories ids.
   */
  private processReferrals(serviceCategories: any[]): void
  {
    if (this.communityEnabled && serviceCategories !== null && serviceCategories.length > 0)
    {
      const messageObject = { categories: serviceCategories, context: 'member' };
      this.messengerService.sendMessage(new MessageSubject('searchForServicesToReferred', messageObject));
    }
    this.refresh.emit();
  }

  /**
   * Get assessment fulfillments from API per the applied filter.
   *
   * @param filter Event filter string.
   */
  private getAssessmentFulfillments(memberId: number): void
  {
    this.state = {
      filter: {
        filters: [
          { field: 'memberId', operator: 'eq', value: memberId },
          { field: 'consentType', operator: 'eq', value: false }    // For now, we hide the consent type assessments
        ], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'lastModifiedOn', dir: 'desc' }
      ],
      take: 5
    };

    // When case is null, it is a member assessment and we need to not show sensitive assessement
    if (this.currentCaseId === null)
    {
      this.state.filter.filters.push({ field: 'caseId', operator: 'isnull', value: '', logic: 'or' });
      this.state.filter.filters.push({ field: 'isSensitive', operator: 'eq', value: false, logic: 'or' });
    }
    else
    {
      this.state.filter.filters.push({ field: 'caseId', operator: 'eq', value: this.currentCaseId, logic: 'or' });
      this.state.filter.filters.push({ field: 'isSensitive', operator: 'eq', value: false, logic: 'or' });
    }
    this.assessmentFulfillmentListService.queryByState(this.state);
  }

  /**
   * This method set the formly fields and model property.
   *
   * @param json Assessement form template.
   */
  private setFormlyFieldsAndModel(json: string): void
  {
    const meta = JSON.parse(Base64Util.decodeUnicode(json)) as FulfillmentMeta;

    // Set fields
    this.form = new UntypedFormGroup({});
    this.fields = meta.fields !== undefined ? meta.fields : [];
    this.fieldsMeta = cloneDeep(this.fields);

    // Set model
    const data = meta.data !== undefined ? meta.data : {};
    for (const [key] of Object.entries(data))
    {
      this.setDateValue(data, key);
    }
    this.model = data;

    // Set formly valueChanges event for sum (if any)
    setTimeout(() => { this.assessmentService.setFormlyControlsSum(this.fields, this.form); });

    // If show member extension, retrieve the member extensions
    if (this.showMemberExtension && this.memberId) 
    {
      this.memberService.getMember(this.memberId).subscribe({
        next: (data: any) =>
        {
          this.patViewExtensions = new GridViewManager(99999);
          this.patViewExtensions.setData(data.extensions ? data.extensions : []);
          this.setViewExtensionsColumnsVisibility();
        },
        error: (errorResponse: any) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * This recursively method set the date and date time value as Date
   * 
   * @param data Data to transform
   * @param key  Key field name
   */
  private setDateValue(data: any, key: string): void 
  {
    if (data[key])
    {
      if (data[key][4] === '-' && data[key][7] === '-' && data[key][10] === 'T')
      {
        data[key] = new Date(data[key]);
      }
      else if (data[key] instanceof Object)
      {
        for (const [childkey] of Object.entries(data[key])) 
        {
          this.setDateValue(data[key], childkey);
        }
      }
    }
  }

  private setViewExtensionsColumnsVisibility(): void 
  {
    this.isShowMemberExtension = this.patViewExtensions.data.length > 0;
    this.viewExtensionsShowDisplayName = this.patViewExtensions.data.filter(x => x.displayName).length > 0;
    this.viewExtensionsShowAlternateValue = this.patViewExtensions.data.filter(x => x.alternateValue).length > 0;
  }
}
